"use client";

import React, { useState } from "react";
import styles from "../css/MergeProfilesPopup.module.css";
import { usePopup } from "../context/PopupContext";
import { mergeProfiles } from "../../../../api/profile/merge_profile";
import { getProfileByPatient } from "../../../../api/profile/create_profile";
import { useAuth } from "../../../../context/AuthContext";

const MergeProfilesPopup = ({ profiles, onClose, overlay = false }) => {
  const { openPopup } = usePopup();
  const { user } = useAuth();

  const [membershipSelections, setMembershipSelections] = useState(
    profiles.map((profile) => (profile.isMainMember ? "main" : "beneficiary"))
  );

  const handleMerge = async () => {
    if (!Array.isArray(profiles) || profiles.length !== 2) return;
    const mainCount = membershipSelections.filter((v) => v === "main").length;
    const benCount = membershipSelections.filter((v) => v === "beneficiary").length;
    if (!(mainCount === 1 && benCount === 1)) {
      alert("Please select one profile as Main Member and the other as Beneficiary before merging.");
      return;
    }

    const mainIndex = membershipSelections.indexOf("main");
    const baseIdx = mainIndex === -1 ? 0 : mainIndex;
    const otherIdx = baseIdx === 0 ? 1 : 0;
    const base = profiles[baseIdx];
    const other = profiles[otherIdx];
    if (!base?.patientId || !other?.patientId) return;
    try {
      const operatorId = user?.userid || user?.id || null;
      await mergeProfiles({
        basePatientId: base.patientId,
        mergePatientId: other.patientId,
        operatorId,
      });

      const mergedProfile = await getProfileByPatient(base.patientId);

      openPopup("success", { mergedProfile });
    } catch (e) {
      alert(e?.message || "Failed to merge profiles");
    }
  };

  const handleMembershipChange = (index, value) => {
    const updatedSelections = [...membershipSelections];
    updatedSelections[index] = value;
    setMembershipSelections(updatedSelections);
  };

  const isValidSelection =
    Array.isArray(profiles) &&
    profiles.length === 2 &&
    membershipSelections.length === 2 &&
    membershipSelections.includes("main") &&
    membershipSelections.includes("beneficiary");

  const content = (
    <div className={styles.popup}>
      <h2 className={styles.title}>Patients Selected</h2>
      <button className={styles.backButton} onClick={onClose}>
        Back
      </button>
      <div className={styles.profilesContainer}>
        {profiles.map((profile, index) => (
          <div key={index} className={styles.profileCard}>
            <div className={styles.profileHeader}>
              <span className={styles.profileIcon}>👤</span>
              <span className={styles.profileNumber}>
                Profile Number: {profile.profileNumber}
              </span>
              <span className={styles.checkmark}>✔</span>
            </div>
            <h3>{profile.name}</h3>
            <div className={styles.details}>
              <div>
                <label>ID Number</label>
                <input type="text" value={profile.idNumber} readOnly />
              </div>
              <div>
                <label>Sex</label>
                <select value={profile.sex} disabled>
                  <option value="M">M</option>
                  <option value="F">F</option>
                </select>
              </div>
              <div>
                <label>Email Address</label>
                <input type="text" value={profile.email} readOnly />
              </div>
              <div>
                <label>Home/Cell Number</label>
                <input type="text" value={profile.phone} readOnly />
              </div>
              <div>
                <label>Postal Address</label>
                <input type="text" value={profile.address} readOnly />
              </div>
              <div>
                <label>Chronic Conditions</label>
                <input
                  type="text"
                  value={profile.chronicConditions || "N/A"}
                  readOnly
                />
              </div>
              <div>
                <label>Medical aid provider</label>
                <input type="text" value={profile.medicalAidProvider} readOnly />
              </div>
              <div>
                <label>Medical aid number</label>
                <input type="text" value={profile.medicalAidNumber} readOnly />
              </div>
              <div>
                <label>Beneficiaries</label>
                <input type="text" value={profile.beneficiaries ?? 0} readOnly />
              </div>
              <div>
                <label>Doctor</label>
                <input type="text" value={profile.doctor} readOnly />
              </div>
              <div>
                <label>Practice Number</label>
                <input type="text" value={profile.practiceNumber} readOnly />
              </div>

              <div className={styles.membership}>
                <label>Membership</label>
                <div className={styles.radioGroup}>
                  <label className={styles.radioOption}>
                    <input
                      type="radio"
                      name={`member-${index}`}
                      value="main"
                      checked={membershipSelections[index] === "main"}
                      onChange={() => handleMembershipChange(index, "main")}
                    />
                    Main Member
                  </label>
                  <label className={styles.radioOption}>
                    <input
                      type="radio"
                      name={`member-${index}`}
                      value="beneficiary"
                      checked={membershipSelections[index] === "beneficiary"}
                      onChange={() => handleMembershipChange(index, "beneficiary")}
                    />
                    Beneficiary
                  </label>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <button className={styles.mergeButton} onClick={handleMerge} disabled={!isValidSelection}>
        Merge Profiles
      </button>
    </div>
  );

  if (overlay) {
    return <div className={styles.overlay}>{content}</div>;
  }
  return content;
};

export default MergeProfilesPopup;